import React from 'react';
import { observer } from 'mobx-react';
import { withNamespaces, Trans } from 'react-i18next';
import styled from 'styled-components';
import PropTypes from 'prop-types';

import { ComponentRegistry } from 'core/index';

import { Section } from 'legacyComponents/layout/section';
import { Button, Spinner } from 'components';
import { media } from 'legacyComponents/styled/styleUtils';

import { modalStore } from 'apps/modals/app';
import { ButtonsWrapper as ButtonsWrapperBase } from 'apps/repository/styles';
import { OpenIssuesTab } from 'apps/issueTracker/views/openIssuesTab';
import { ClosedIssuesTab } from 'apps/issueTracker/views/closedIssuesTab';
import { LabelsTab } from 'apps/issueTracker/views/labelsTab';
import { ColorOption } from 'apps/issueTracker/views/colorOption';
import { ColorSelection } from 'apps/issueTracker/views/colorSelection';
import { LabelItem } from 'apps/issueTracker/views/labelItem';
import { NewLabelForm } from 'apps/issueTracker/views/newLabelForm';
import { themes } from 'styles/themes/wikifactoryNew';

ComponentRegistry.add('issueTracker.colorOption', ColorOption);
ComponentRegistry.add('issueTracker.colorSelection', ColorSelection);
ComponentRegistry.add('issueTracker.labelItem', LabelItem);

const ButtonsWrapper = styled(ButtonsWrapperBase)`
  ${media.xs`
  display: block;
`};
`;

type IssueTrackerProps = {
  title: string;
  project: Record<string, any>;
  projectManager: Record<string, any>;
  manager: Record<string, any>;
};

@withNamespaces()
@observer
export class IssueTracker extends React.Component<IssueTrackerProps> {
  clickOpenIssue = () => {
    this.props.manager.OpenIssueForm();
  };

  clickNewLabel = () => {
    const {
      manager: { issueTracker },
    } = this.props;
    modalStore.SetModalComponent(NewLabelForm, { issueTracker });
  };

  render() {
    const { manager, projectManager } = this.props;
    const { issueTracker } = manager;
    const { project } = projectManager;
    if (issueTracker === null || project === null) {
      return (
        <Section title='Open Tasks'>
          <Spinner margin='0 auto' />
        </Section>
      );
    }

    return (
      <>
        <Section
          title='Open Tasks'
          headerWrapperStyle={{
            borderBottom: `1px solid ${themes.color.wfGrey100}`,
            paddingBottom: 18,
          }}
          subTitle={`(${issueTracker.openIssuesCount})`}
          dataTestId='projectPage-openTasks-section'
        >
          <ButtonsWrapper>
            <Button
              text={<Trans>New Task</Trans>}
              size='small'
              icon='add'
              outline
              iconPosition='right'
              data-testid='projectPage-createTask-button'
              onClick={this.clickOpenIssue}
            />
          </ButtonsWrapper>
          <OpenIssuesTab
            app={issueTracker}
            manager={manager}
            project={project}
          />
        </Section>
        <Section
          title='Closed Tasks'
          subTitle={`(${issueTracker.closedIssuesCount})`}
          dataTestId='projectPage-closedTasks-section'
          headerWrapperStyle={{
            borderBottom: `1px solid ${themes.color.wfGrey100}`,
            paddingBottom: 18,
          }}
        >
          <ClosedIssuesTab
            app={issueTracker}
            manager={manager}
            project={project}
          />
        </Section>
        <Section title='Labels' last>
          {project && project.inSpace.whichTypes.includes('TrackerLabel') && (
            <ButtonsWrapper>
              <Button
                text={<Trans>New Label</Trans>}
                size='small'
                onClick={this.clickNewLabel}
                icon='add'
                outline
                data-testid='project-createLabel-button'
              />
            </ButtonsWrapper>
          )}
          <LabelsTab
            dataTestId='projectPage-labels-section'
            app={issueTracker}
            manager={manager}
            project={project}
          />
        </Section>
      </>
    );
  }
}
